import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Phone, Mail, MapPin, CheckCircle, TrendingUp, Users, Zap, Shield, BarChart3, Clock, MessageSquare, Calendar, Bot, Sparkles, ArrowRight, Play, Star, Menu, X } from 'lucide-react';

const LandingPage = () => {
  const [activeTab, setActiveTab] = useState('tech');
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    
    // Set page metadata for SEO
    document.title = 'AI Sales Assistant - Convert Leads 24/7 | Automated Lead Generation & CRM Integration';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.content = 'Transform your sales with AI-powered lead generation. 98% faster response times, 65% booking rate increase. Book more calls on autopilot with our 24/7 AI sales assistant.';
    }
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const stats = [
    { value: '98%', label: 'Faster Response Times', icon: <Zap className="w-6 h-6" /> },
    { value: '65%', label: 'Increase in Booking Rate', icon: <TrendingUp className="w-6 h-6" /> },
    { value: '57%', label: 'More Qualified Leads', icon: <Users className="w-6 h-6" /> },
    { value: '24/7', label: 'Always Available', icon: <Clock className="w-6 h-6" /> }
  ];

  const features = [
    {
      icon: <Bot className="w-8 h-8" />,
      title: 'AI-Powered Conversations',
      description: 'Natural, human-like conversations that qualify leads and book appointments automatically'
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      title: 'Smart Calendar Integration',
      description: 'Seamlessly integrates with your calendar to book meetings without double-booking'
    },
    {
      icon: <MessageSquare className="w-8 h-8" />,
      title: 'Omnichannel Support',
      description: 'Works across SMS, email, web chat, and social media for maximum reach'
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: 'Real-Time Analytics',
      description: 'Track conversions, response times, and ROI with comprehensive dashboards'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Enterprise Security',
      description: 'Bank-level encryption and GDPR compliance to protect your data'
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: 'Custom AI Training',
      description: 'Tailored to your business with your tone, knowledge, and sales process'
    }
  ];

  const integrations = [
    'Salesforce', 'HubSpot', 'Pipedrive', 'Zoho CRM', 'Monday.com',
    'ActiveCampaign', 'Zendesk', 'Insightly', 'HighLevel', 'Nutshell'
  ];

  const faqs = [
    {
      q: 'How does the AI Sales Assistant work?',
      a: 'Our AI assistant uses advanced natural language processing to engage with your leads 24/7. It qualifies prospects, answers questions, handles objections, and automatically books qualified leads into your calendar - all while maintaining natural, human-like conversations.'
    },
    {
      q: 'Can it integrate with my existing CRM?',
      a: 'Yes! We integrate seamlessly with all major CRMs including Salesforce, HubSpot, Pipedrive, and 30+ other platforms. Your lead data syncs automatically in real-time.'
    },
    {
      q: 'What industries do you work with?',
      a: 'We work with businesses across all industries including real estate, automotive, SaaS, professional services, healthcare, and education. Our AI can be customized for any sales process.'
    },
    {
      q: 'How long does setup take?',
      a: 'Most clients are fully operational within 48 hours. Our team handles the entire setup, AI training, and integration process for you.'
    },
    {
      q: 'What kind of support do I get?',
      a: 'You get dedicated onboarding, 24/7 technical support, regular optimization sessions, and a success manager to ensure you achieve your sales goals.'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      role: 'CEO, TechStart Solutions',
      text: 'Our booking rates increased by 73% in the first month. The AI handles all our initial qualifying, giving our sales team more time to close deals.',
      rating: 5
    },
    {
      name: 'Michael Chen',
      role: 'VP Sales, Global Enterprises',
      text: 'Game-changer for our outbound campaigns. We\'re converting cold leads we would have written off before.',
      rating: 5
    },
    {
      name: 'Jennifer Adams',
      role: 'Director of Marketing, EstateProMax',
      text: 'The ROI is incredible. We\'re booking 5x more appointments with the same ad spend.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-gray-900/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Bot className="w-8 h-8 text-blue-500" />
              <span className="text-xl font-bold">AI Sales Pro</span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="hover:text-blue-400 transition-colors">Features</a>
              <a href="#how-it-works" className="hover:text-blue-400 transition-colors">How it Works</a>
              <a href="#pricing" className="hover:text-blue-400 transition-colors">Pricing</a>
              <a href="#testimonials" className="hover:text-blue-400 transition-colors">Testimonials</a>
              <button className="bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded-lg font-semibold transition-all transform hover:scale-105">
                Book Demo
              </button>
            </div>

            <button 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-gray-900/95 backdrop-blur-md border-t border-gray-700">
            <div className="px-4 py-4 space-y-3">
              <a href="#features" className="block hover:text-blue-400">Features</a>
              <a href="#how-it-works" className="block hover:text-blue-400">How it Works</a>
              <a href="#pricing" className="block hover:text-blue-400">Pricing</a>
              <a href="#testimonials" className="block hover:text-blue-400">Testimonials</a>
              <button className="w-full bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded-lg font-semibold">
                Book Demo
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 blur-3xl"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center bg-blue-600/20 border border-blue-500/30 rounded-full px-4 py-2">
                <Sparkles className="w-4 h-4 mr-2 text-blue-400" />
                <span className="text-sm">Never Miss a Sales Lead Again</span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                Turn Leads Into <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Booked Sales Calls</span> With AI
              </h1>
              
              <p className="text-xl text-gray-300">
                Our AI Sales Assistant engages, qualifies, and books appointments 24/7. 
                Increase your booking rate by 65% without adding headcount.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 px-8 py-4 rounded-lg font-semibold text-lg transition-all transform hover:scale-105 shadow-lg hover:shadow-blue-500/25">
                  Start Free Trial
                </button>
                <button className="border border-gray-600 hover:border-gray-500 px-8 py-4 rounded-lg font-semibold text-lg transition-all flex items-center justify-center gap-2">
                  <Play className="w-5 h-5" /> Watch Demo
                </button>
              </div>
              
              <div className="flex items-center gap-6 pt-4">
                <div className="flex -space-x-2">
                  {[1,2,3,4,5].map(i => (
                    <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-400 border-2 border-gray-900"></div>
                  ))}
                </div>
                <div>
                  <div className="flex text-yellow-400">
                    {[1,2,3,4,5].map(i => <Star key={i} className="w-5 h-5 fill-current" />)}
                  </div>
                  <p className="text-sm text-gray-400">500+ businesses automated</p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-2xl p-8 backdrop-blur-sm border border-gray-700">
                <div className="space-y-4">
                  <div className="bg-gray-800/50 rounded-lg p-4 animate-pulse">
                    <div className="flex items-start gap-3">
                      <Bot className="w-8 h-8 text-blue-400 mt-1" />
                      <div className="flex-1">
                        <p className="text-sm text-blue-400 mb-1">AI Assistant</p>
                        <p>Hi! I noticed you're interested in our services. I can help you find the perfect solution. What's your biggest sales challenge right now?</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-800/30 rounded-lg p-4">
                    <div className="flex items-start gap-3 justify-end">
                      <div className="flex-1 text-right">
                        <p className="text-sm text-green-400 mb-1">Lead</p>
                        <p>We're struggling to respond to leads quickly enough</p>
                      </div>
                      <div className="w-8 h-8 bg-green-500 rounded-full"></div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-800/50 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Bot className="w-8 h-8 text-blue-400 mt-1" />
                      <div className="flex-1">
                        <p className="text-sm text-blue-400 mb-1">AI Assistant</p>
                        <p>That's exactly what we solve! Our AI responds in under 30 seconds, 24/7. Most clients see 98% faster response times. Would you like to see how it works for your industry?</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 border-y border-gray-800 bg-gray-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center space-y-2">
                <div className="flex justify-center text-blue-400 mb-3">{stat.icon}</div>
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                  {stat.value}
                </div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              Intelligent AI Solutions That <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Transform</span> Your Sales
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Everything you need to automate your sales process and close more deals
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <div key={idx} className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700 hover:border-blue-500/50 transition-all hover:transform hover:scale-105">
                <div className="text-blue-400 mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-4 bg-gray-800/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              Filling Your Sales Pipeline in <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">3 Simple Steps</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-semibold">Custom AI Setup</h3>
              <p className="text-gray-400">We train your AI on your business, products, and sales process in 48 hours</p>
            </div>
            
            <div className="text-center space-y-4">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-semibold">Lead Engagement</h3>
              <p className="text-gray-400">AI instantly responds to leads, qualifies them, and handles objections naturally</p>
            </div>
            
            <div className="text-center space-y-4">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-semibold">Calendar Booking</h3>
              <p className="text-gray-400">Qualified leads are automatically booked into your calendar with all context</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              What Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Clients Say</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
                <div className="flex text-yellow-400 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-current" />
                  ))}
                </div>
                <p className="text-gray-300 mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-semibold">{testimonial.name}</p>
                  <p className="text-sm text-gray-400">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="py-16 px-4 border-y border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              Collaborating with <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Leading Platforms</span>
            </h2>
          </div>
          
          <div className="flex flex-wrap justify-center gap-6">
            {integrations.map((integration, idx) => (
              <div key={idx} className="bg-gray-800/30 px-6 py-3 rounded-lg border border-gray-700 hover:border-blue-500/50 transition-colors">
                {integration}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Frequently Asked <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Questions</span>
            </h2>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-800/70 transition-colors"
                >
                  <span className="font-semibold">{faq.q}</span>
                  {openFaq === idx ? <ChevronUp /> : <ChevronDown />}
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-4 text-gray-400">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600/20 to-purple-600/20">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl font-bold">
            Ready to Get the Perks of a 24/7 Sales Team <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">WITHOUT the Expense?</span>
          </h2>
          <p className="text-xl text-gray-300">
            Join 500+ businesses that have automated their sales process
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 px-8 py-4 rounded-lg font-semibold text-lg transition-all transform hover:scale-105 shadow-lg hover:shadow-blue-500/25">
              Start Your Free Trial
            </button>
            <button className="border border-gray-600 hover:border-gray-500 px-8 py-4 rounded-lg font-semibold text-lg transition-all">
              Schedule a Demo
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Bot className="w-8 h-8 text-blue-500" />
                <span className="text-xl font-bold">AI Sales Pro</span>
              </div>
              <p className="text-gray-400 text-sm">
                Transform your sales process with AI-powered lead generation and qualification.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Home</a></li>
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API Reference</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Support Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact Us</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>1-800-AI-SALES</span>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>support@aisalespro.com</span>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>San Francisco, CA</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © 2025 AI Sales Pro. All rights reserved.
            </p>
            <div className="flex gap-6 text-gray-400 text-sm">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="/sitemap.xml" className="hover:text-white transition-colors">Sitemap</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;